import React, {FunctionComponent, useCallback, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, Theme} from "@emotion/react";

import {Container} from "@pg-design/grid-module";
import {calculateRemSize, display, mb, mt, onDesktop, ph} from "@pg-design/helpers-css";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {useElementWasVisible} from "@pg-mono/hooks";

import {plansUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {ApplicationSource} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {DetailConstructionGallerySection} from "../../../atoms/DetailConstructionGallerySection";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {useTransportPois} from "../../../maps/hooks/use_transport_pois";
import {IPoi} from "../../../maps/types/IPoi";
import {TransportPoiType} from "../../../maps/utils/PoiType";
import {initializePlansScript, PLANS_SCRIPT_ID} from "../../../plans/initialize_plans_script";
import {Promotions} from "../../../promotion/components/Promotions";
import {OSM_MAP_CONTAINER} from "../../../property/components/PropertyDetailForSale";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {Country} from "../../../region/types/Country";
import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {TagType} from "../../../types/TagType";
import {ViewType} from "../../../view_type/ViewType";
import {OfferNavBar} from "../../atoms/OfferNavBar";
import {OfferSalesOffices} from "../../atoms/OfferSalesOffices";
import {OfferUVP} from "../../atoms/OfferUVP";
import {DetailNearbyOffers} from "../../components/DetailNearbyOffers";
import {OfferDetailOpinions} from "../../components/opinions/OfferDetailOpinions";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {IOfferDetailPromotion} from "../actions/fetch_offer_detail_promotions";
import {PropertyListTabTypes} from "../actions/fetch_offer_detail_properties";
import {OFFER_CHAT_BOT_TRIGGER} from "../constants/chat_bot_trigger";
import {OfferDetailPreSaleForm} from "../OfferDetailPreSaleForm";
import {OfferDetailPropertyList} from "./property_list/OfferDetailPropertyList";
import {OfferDetailStages} from "./stages/OfferDetailStages";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailCurrentInfo} from "./OfferDetailCurrentInfo";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {OfferDetailDetails} from "./OfferDetailDetails";
import {OfferDetailGetHomeLink} from "./OfferDetailGetHomeLink";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferDetailLocationMap} from "./OfferDetailLocationMap";
import {OfferDetailLocationSummary} from "./OfferDetailLocationSummary";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[];
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    refElems: {
        refGallery: React.RefObject<HTMLTableSectionElement | null>;
        refProperties: React.RefObject<HTMLTableSectionElement | null>;
        refDescription: React.RefObject<HTMLTableSectionElement | null>;
        refPromotions: React.RefObject<HTMLTableSectionElement | null>;
        refLocation: React.RefObject<HTMLTableSectionElement | null>;
        refContact: React.RefObject<HTMLTableSectionElement | null>;
    };
    visibleElement: string | null;
    viewportHeight?: number;
    changeVisibility: (name: string) => void;
    changeModalTab: (tab: OfferModalActiveTab) => void;
    hasDescription: boolean;
    hasProperties: boolean;
    shouldMapBeRendered?: boolean;
}

interface IValueLabel {
    value: number;
    label: string;
}

export const OfferDetailForSale: FunctionComponent<IProps> = React.memo((props) => {
    const {offer, promotions, variables, refElems, visibleElement, changeModalTab, hasDescription, hasProperties} = props;
    const [currentFilters, setCurrentFilter] = useState<ISearchFiltersFormValues | null>(null);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const rankingItem = getRankingItem(offer.id);

    /**
     * Plans
     */

    const [isPlanScriptLoading, setIsPlanScriptLoading] = useState(false);
    const onPlansOpenClick = async () => {
        if (!plansUrl) {
            console.error("plansUrl is not defined");
            return;
        }

        if (!viewType) {
            console.error("viewType is not defined");
            return;
        }

        const source = 3;
        const {site, site_id} = getTrackedSiteData();

        setIsPlanScriptLoading(true);

        await initializePlansScript(
            offer.id,
            source,
            plansUrl,
            {
                view_type: viewType,
                site,
                site_id
            },
            {
                renderApplicationButton: () => {
                    return <span />;
                },
                renderFavouriteButton: () => {
                    return <span />;
                }
            }
        );

        setIsPlanScriptLoading(false);
    };

    /**
     * Rest
     */

    const hasPromotions = promotions.length > 0;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const scrollToRef = (ref: React.RefObject<HTMLTableSectionElement | null>) =>
        ref.current && ref.current.scrollIntoView({behavior: "smooth", block: "start", inline: "center"});

    const scrollTo = useCallback(
        (_: number, refElem: React.RefObject<HTMLTableSectionElement | null>) => (e: React.MouseEvent) => {
            e.preventDefault();
            scrollToRef(refElem);
        },
        [visibleElement]
    );

    const location = useLocation();

    const contactBarProps = {
        trackOffer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        trackVendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        }
    };

    const propertyListObj = usePropertyList(props.offer.id, PropertyListTabTypes.FILTER, refElems.refProperties);

    const isPreSale = offer.configuration.pre_sale;
    const showPriceAndContactButtons = isOfferForSale(offer);
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);
    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );
    const standardType = !!variables?.offer_standard_description_type ? variables.offer_standard_description_type : "Informacja u dewelopera";

    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    const transportPoisFromApi = (transportPoiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });

    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    return (
        <>
            <OfferDetailStages offer={offer} />

            <OfferNavBar
                css={offerNavBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                refElements={refElems}
                offer={offer}
                promotions={promotions}
                onButtonClick={changeModalTab}
                // plans
                isPlanScriptLoading={isPlanScriptLoading}
                onPlansOpenClick={onPlansOpenClick}
            />
            <div id={PLANS_SCRIPT_ID} />

            <OfferUVP offer={offer} standardType={standardType} />

            <Container>
                {(hasProperties || isPreSale) && (
                    <section id="nieruchomosci" ref={refElems.refProperties} css={propertyListSectionStyles}>
                        {isPreSale ? (
                            <OfferDetailPreSaleForm offer={props.offer} />
                        ) : (
                            <OfferDetailPropertyList
                                offer={offer}
                                offerId={props.offer.id}
                                onFilterValuesChange={setCurrentFilter}
                                propertyListObj={propertyListObj}
                                propertyListType={PropertyListType.TILES}
                            />
                        )}
                    </section>
                )}
                {/* Currently GetHome has only offers from Poland */}
                {offer.region.country === Country.POLAND && (
                    <section>
                        <OfferDetailGetHomeLink
                            filterData={currentFilters}
                            offer={offer}
                            region={offer.region.name}
                            regionId={offer.region.id}
                            stats={offer.region.stats}
                        />
                    </section>
                )}

                <Container>
                    {hasPromotions && (
                        <section id="promocje" ref={refElems.refPromotions} css={promotionsStyle}>
                            <Promotions
                                promotions={promotions}
                                vendorId={offer.vendor.id}
                                offerId={offer.id}
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                isSaleOverLimit={getOfferDisplayStatus(offer) === "limited"}
                            />
                        </section>
                    )}
                </Container>

                <section css={opinionsWrapper}>
                    <OfferDetailOpinions offer={offer} />
                </section>
            </Container>

            {props.shouldMapBeRendered ? (
                <section id="okolica-mapa" css={[mt(4), mb(2), mapSectionContainer]} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded && <OfferDetailLocationMap offer={offer} onFullscreenClick={() => props.changeModalTab(OfferModalActiveTab.MAP)} />}
                </section>
            ) : null}

            <Container>
                <Container>
                    <div css={[mb(3), ph(1.5), onDesktop(ph(3))]}>
                        <OfferDetailLocationSummary
                            changeModalTab={props.changeModalTab}
                            offer={offer}
                            geoAreaCoordinates={offer.geo_area.coordinates}
                            geoPointCoordinates={offer.geo_point.coordinates}
                            routes={routes}
                        />
                    </div>

                    <div id={OFFER_CHAT_BOT_TRIGGER}>
                        <section css={detailConstructionSectionStyle}>
                            <DetailConstructionGallerySection onButtonClick={() => changeModalTab(OfferModalActiveTab.CONSTRUCTION_GALLERY)} type="offer" />
                        </section>
                        {groupedOfferAmenities.length > 0 && (
                            <section css={offerDetailAmenitiesSection}>
                                <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                            </section>
                        )}
                    </div>

                    <section css={detailsSectionMargin}>
                        <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                    </section>
                    <section css={detailsSectionMargin}>
                        <OfferDetailCurrentInfo
                            offer={offer}
                            variables={variables}
                            showPriceAndContactButtons={showPriceAndContactButtons}
                            changeVisibility={(e) => {
                                props.changeVisibility("properties");
                                location.hash = "nieruchomosci";
                                propertyListObj.onTabChange(PropertyListTabTypes.INVESTMENT, ViewType.OFFER);
                                scrollTo(300, refElems.refProperties)(e);
                            }}
                        />
                    </section>
                    {rankingItem && (
                        <section id="ranking" css={detailsSectionMargin}>
                            <OfferRankingModule rankingItem={rankingItem} />
                        </section>
                    )}
                    {hasDescription && (
                        <section id="opis" ref={refElems.refDescription} css={detailsSectionMargin}>
                            <OfferDetailDescription offer={offer} />
                        </section>
                    )}
                    <section id="okolica" ref={refElems.refLocation}>
                        {transportPoiData && transportPoiData.poi && (
                            <OfferDetailLocation
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                                changeModalTab={changeModalTab}
                                pois={transportPoiData.poi.pois}
                            />
                        )}
                    </section>
                    <section id="timestamp" css={timeStampSectionStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                    </section>
                    <section id="kontakt" ref={refElems.refContact} css={contactSectionStyle}>
                        <OfferSalesOffices offer={offer} />
                    </section>
                </Container>
            </Container>

            <DetailNearbyOffers css={nearbyOffersHolder} viewType={ViewType.OFFER} offer={offer} />
            {showPriceAndContactButtons && (
                <ContactBar
                    salesOffices={offer.sales_offices}
                    vendor={offer.vendor}
                    trackVendor={contactBarProps.trackVendor}
                    trackOffer={contactBarProps.trackOffer}
                    phoneClip={offer.configuration.phone_clip}
                    isPhoneClipEnabled={offer.is_phone_clip_enabled}
                    source={ApplicationSource.Offer}
                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                    offer={offer}
                />
            )}
        </>
    );
});

const propertyListSectionStyles = (theme: Theme) => css`
    position: relative;
    z-index: 1;

    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const contactSectionStyle = css`
    ${mb(2)};
`;

const nearbyOffersHolder = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(10)}
    }
`;

const offerNavBarWrapperStyle = (theme: Theme) => css`
    margin: 0 0 0.3rem 0;
    ${display("none")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
        ${display("block")};
    }
`;

const navBarContentStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) and (max-width: 1310px) {
        margin-top: 60px; // other bars height
        border-top: 1px solid ${getThemeVariable("colors.gray.400")};
    }
`;

const promotionsStyle = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 6rem 0;
        ${mb(7)};
    }
`;

const detailsSectionMargin = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const opinionsWrapper = css`
    ${mt(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        width: calc(288px * 3 + 1.6rem * 2); // PropertyBox width times 3 + margin between boxes times 2
        margin: ${calculateRemSize(3)} auto 0 auto;
    }
`;

const timeStampSectionStyle = (theme: Theme) => css`
    ${mb(1)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)}
    }
`;

const detailConstructionSectionStyle = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const offerDetailAmenitiesSection = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;
const mapSectionContainer = (theme: Theme) => css`
    min-height: 58rem;
    @media (min-width: ${theme.breakpoints.md}) {
        min-height: 55rem;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        min-height: 68rem;
    }
`;
import React, {FunctionComponent, useCallback, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router";
import {css, Theme} from "@emotion/react";

import {Container} from "@pg-design/grid-module";
import {calculateRemSize, display, mb, mt, onDesktop, ph} from "@pg-design/helpers-css";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {useElementWasVisible} from "@pg-mono/hooks";

import {plansUrl} from "../../../app/read_rp_environment_variables";
import {IRPStore} from "../../../app/rp_reducer";
import {ApplicationSource} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {DetailConstructionGallerySection} from "../../../atoms/DetailConstructionGallerySection";
import {TimeStamp} from "../../../atoms/TimeStamp";
import {useTransportPois} from "../../../maps/hooks/use_transport_pois";
import {IPoi} from "../../../maps/types/IPoi";
import {TransportPoiType} from "../../../maps/utils/PoiType";
import {initializePlansScript, PLANS_SCRIPT_ID} from "../../../plans/initialize_plans_script";
import {Promotions} from "../../../promotion/components/Promotions";
import {OSM_MAP_CONTAINER} from "../../../property/components/PropertyDetailForSale";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {OfferRankingModule} from "../../../ranking/components/OfferRankingModule";
import {getRankingItem} from "../../../ranking/utils/get_ranking_item";
import {Country} from "../../../region/types/Country";
import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {TagType} from "../../../types/TagType";
import {ViewType} from "../../../view_type/ViewType";
import {OfferNavBar} from "../../atoms/OfferNavBar";
import {OfferSalesOffices} from "../../atoms/OfferSalesOffices";
import {OfferUVP} from "../../atoms/OfferUVP";
import {DetailNearbyOffers} from "../../components/DetailNearbyOffers";
import {OfferDetailOpinions} from "../../components/opinions/OfferDetailOpinions";
import {getOfferAmenities} from "../../helpers/get_offer_amenities";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferTag} from "../../types/IOfferTag";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {getOfferDisplayStatus} from "../../utils/get_offer_display_status";
import {isOfferForSale} from "../../utils/is_offer_for_sale";
import {IOfferDetailPromotion} from "../actions/fetch_offer_detail_promotions";
import {PropertyListTabTypes} from "../actions/fetch_offer_detail_properties";
import {OFFER_CHAT_BOT_TRIGGER} from "../constants/chat_bot_trigger";
import {OfferDetailPreSaleForm} from "../OfferDetailPreSaleForm";
import {OfferDetailPropertyList} from "./property_list/OfferDetailPropertyList";
import {OfferDetailStages} from "./stages/OfferDetailStages";
import {OfferDetailAmenities} from "./OfferDetailAmenities";
import {OfferDetailCurrentInfo} from "./OfferDetailCurrentInfo";
import {OfferDetailDescription} from "./OfferDetailDescription";
import {OfferDetailDetails} from "./OfferDetailDetails";
import {OfferDetailGetHomeLink} from "./OfferDetailGetHomeLink";
import {OfferDetailLocation} from "./OfferDetailLocation";
import {OfferDetailLocationMap} from "./OfferDetailLocationMap";
import {OfferDetailLocationSummary} from "./OfferDetailLocationSummary";

interface IProps {
    offer: IOfferDetail;
    promotions: IOfferDetailPromotion[];
    variables: {
        offer_natural_sites: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_funds_protection: IValueLabel[];
    } | null;
    refElems: {
        refGallery: React.RefObject<HTMLTableSectionElement | null>;
        refProperties: React.RefObject<HTMLTableSectionElement | null>;
        refDescription: React.RefObject<HTMLTableSectionElement | null>;
        refPromotions: React.RefObject<HTMLTableSectionElement | null>;
        refLocation: React.RefObject<HTMLTableSectionElement | null>;
        refContact: React.RefObject<HTMLTableSectionElement | null>;
    };
    visibleElement: string | null;
    viewportHeight?: number;
    changeVisibility: (name: string) => void;
    changeModalTab: (tab: OfferModalActiveTab) => void;
    hasDescription: boolean;
    hasProperties: boolean;
    shouldMapBeRendered?: boolean;
}

interface IValueLabel {
    value: number;
    label: string;
}

export const OfferDetailForSale: FunctionComponent<IProps> = React.memo((props) => {
    const {offer, promotions, variables, refElems, visibleElement, changeModalTab, hasDescription, hasProperties} = props;
    const [currentFilters, setCurrentFilter] = useState<ISearchFiltersFormValues | null>(null);
    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const rankingItem = getRankingItem(offer.id);

    /**
     * Plans
     */

    const [isPlanScriptLoading, setIsPlanScriptLoading] = useState(false);
    const onPlansOpenClick = async () => {
        if (!plansUrl) {
            console.error("plansUrl is not defined");
            return;
        }

        if (!viewType) {
            console.error("viewType is not defined");
            return;
        }

        const source = 3;
        const {site, site_id} = getTrackedSiteData();

        setIsPlanScriptLoading(true);

        await initializePlansScript(
            offer.id,
            source,
            plansUrl,
            {
                view_type: viewType,
                site,
                site_id
            },
            {
                renderApplicationButton: () => {
                    return <span />;
                },
                renderFavouriteButton: () => {
                    return <span />;
                }
            }
        );

        setIsPlanScriptLoading(false);
    };

    /**
     * Rest
     */

    const hasPromotions = promotions.length > 0;

    const publishDate = !offer.configuration.hide_publish_date && offer.configuration.publish_date ? offer.configuration.publish_date : null;

    const scrollToRef = (ref: React.RefObject<HTMLTableSectionElement | null>) =>
        ref.current && ref.current.scrollIntoView({behavior: "smooth", block: "start", inline: "center"});

    const scrollTo = useCallback(
        (_: number, refElem: React.RefObject<HTMLTableSectionElement | null>) => (e: React.MouseEvent) => {
            e.preventDefault();
            scrollToRef(refElem);
        },
        [visibleElement]
    );

    const location = useLocation();

    const contactBarProps = {
        trackOffer: {
            geo_point: {
                coordinates: offer.geo_point.coordinates
            },
            id: offer.id,
            name: offer.name,
            type: offer.type,
            region: {
                country: offer.region.country,
                full_name: offer.region.full_name,
                id: offer.region.id
            }
        },
        trackVendor: {
            id: offer.vendor.id,
            name: offer.vendor.name,
            slug: offer.vendor.slug
        }
    };

    const propertyListObj = usePropertyList(props.offer.id, PropertyListTabTypes.FILTER, refElems.refProperties);

    const isPreSale = offer.configuration.pre_sale;
    const showPriceAndContactButtons = isOfferForSale(offer);
    const getNearbyParksTag = offer.tags.filter((tag: IOfferTag) => tag.id === TagType.GREEN_AREAS);
    const groupedOfferAmenities = useMemo(
        () => getOfferAmenities(offer.facilities, offer.natural_sites, getNearbyParksTag, offer.standard_description.types, variables),
        [offer.facilities, offer.natural_sites, offer.standard_description.types, variables]
    );
    const standardType = !!variables?.offer_standard_description_type ? variables.offer_standard_description_type : "Informacja u dewelopera";

    const transportPoiData = useSelector((state: IRPStore) => state.offerDetail.transportPoi);

    const transportPoisFromApi = (transportPoiData?.poi?.pois.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const {routes} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });

    const [canMapBeLoaded, setCanMapBeLoaded] = useState(false);

    useElementWasVisible({
        ids: [OSM_MAP_CONTAINER],
        onElementVisible: () => {
            setCanMapBeLoaded(true);
        },
        once: true,
        threshold: 0.05
    });

    return (
        <>
            <OfferDetailStages offer={offer} />

            <OfferNavBar
                css={offerNavBarWrapperStyle}
                navBarContentStyle={navBarContentStyle}
                refElements={refElems}
                offer={offer}
                promotions={promotions}
                onButtonClick={changeModalTab}
                // plans
                isPlanScriptLoading={isPlanScriptLoading}
                onPlansOpenClick={onPlansOpenClick}
            />
            <div id={PLANS_SCRIPT_ID} />

            <OfferUVP offer={offer} standardType={standardType} />

            <Container>
                {(hasProperties || isPreSale) && (
                    <section id="nieruchomosci" ref={refElems.refProperties} css={propertyListSectionStyles}>
                        {isPreSale ? (
                            <OfferDetailPreSaleForm offer={props.offer} />
                        ) : (
                            <OfferDetailPropertyList
                                offer={offer}
                                offerId={props.offer.id}
                                onFilterValuesChange={setCurrentFilter}
                                propertyListObj={propertyListObj}
                                propertyListType={PropertyListType.TILES}
                            />
                        )}
                    </section>
                )}
                {/* Currently GetHome has only offers from Poland */}
                {offer.region.country === Country.POLAND && (
                    <section>
                        <OfferDetailGetHomeLink
                            filterData={currentFilters}
                            offer={offer}
                            region={offer.region.name}
                            regionId={offer.region.id}
                            stats={offer.region.stats}
                        />
                    </section>
                )}

                <Container>
                    {hasPromotions && (
                        <section id="promocje" ref={refElems.refPromotions} css={promotionsStyle}>
                            <Promotions
                                promotions={promotions}
                                vendorId={offer.vendor.id}
                                offerId={offer.id}
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                isSaleOverLimit={getOfferDisplayStatus(offer) === "limited"}
                            />
                        </section>
                    )}
                </Container>

                <section css={opinionsWrapper}>
                    <OfferDetailOpinions offer={offer} />
                </section>
            </Container>

            {props.shouldMapBeRendered ? (
                <section id="okolica-mapa" css={[mt(4), mb(2), mapSectionContainer]} data-id={OSM_MAP_CONTAINER}>
                    {canMapBeLoaded && <OfferDetailLocationMap offer={offer} onFullscreenClick={() => props.changeModalTab(OfferModalActiveTab.MAP)} />}
                </section>
            ) : null}

            <Container>
                <Container>
                    <div css={[mb(3), ph(1.5), onDesktop(ph(3))]}>
                        <OfferDetailLocationSummary
                            changeModalTab={props.changeModalTab}
                            offer={offer}
                            geoAreaCoordinates={offer.geo_area.coordinates}
                            geoPointCoordinates={offer.geo_point.coordinates}
                            routes={routes}
                        />
                    </div>

                    <div id={OFFER_CHAT_BOT_TRIGGER}>
                        <section css={detailConstructionSectionStyle}>
                            <DetailConstructionGallerySection onButtonClick={() => changeModalTab(OfferModalActiveTab.CONSTRUCTION_GALLERY)} type="offer" />
                        </section>
                        {groupedOfferAmenities.length > 0 && (
                            <section css={offerDetailAmenitiesSection}>
                                <OfferDetailAmenities offerAmenities={groupedOfferAmenities} />
                            </section>
                        )}
                    </div>

                    <section css={detailsSectionMargin}>
                        <OfferDetailDetails offer={offer} variables={variables} showAmenities={groupedOfferAmenities.length === 0} />
                    </section>
                    <section css={detailsSectionMargin}>
                        <OfferDetailCurrentInfo
                            offer={offer}
                            variables={variables}
                            showPriceAndContactButtons={showPriceAndContactButtons}
                            changeVisibility={(e) => {
                                props.changeVisibility("properties");
                                location.hash = "nieruchomosci";
                                propertyListObj.onTabChange(PropertyListTabTypes.INVESTMENT, ViewType.OFFER);
                                scrollTo(300, refElems.refProperties)(e);
                            }}
                        />
                    </section>
                    {rankingItem && (
                        <section id="ranking" css={detailsSectionMargin}>
                            <OfferRankingModule rankingItem={rankingItem} />
                        </section>
                    )}
                    {hasDescription && (
                        <section id="opis" ref={refElems.refDescription} css={detailsSectionMargin}>
                            <OfferDetailDescription offer={offer} />
                        </section>
                    )}
                    <section id="okolica" ref={refElems.refLocation}>
                        {transportPoiData && transportPoiData.poi && (
                            <OfferDetailLocation
                                offer={offer}
                                geoAreaCoordinates={offer.geo_area.coordinates}
                                geoPointCoordinates={offer.geo_point.coordinates}
                                changeModalTab={changeModalTab}
                                pois={transportPoiData.poi.pois}
                            />
                        )}
                    </section>
                    <section id="timestamp" css={timeStampSectionStyle}>
                        <TimeStamp lastUpdateDate={offer.stats?.last_update ?? ""} publishDate={publishDate} />
                    </section>
                    <section id="kontakt" ref={refElems.refContact} css={contactSectionStyle}>
                        <OfferSalesOffices offer={offer} />
                    </section>
                </Container>
            </Container>

            <DetailNearbyOffers css={nearbyOffersHolder} viewType={ViewType.OFFER} offer={offer} />
            {showPriceAndContactButtons && (
                <ContactBar
                    salesOffices={offer.sales_offices}
                    vendor={offer.vendor}
                    trackVendor={contactBarProps.trackVendor}
                    trackOffer={contactBarProps.trackOffer}
                    phoneClip={offer.configuration.phone_clip}
                    isPhoneClipEnabled={offer.is_phone_clip_enabled}
                    source={ApplicationSource.Offer}
                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                    offer={offer}
                />
            )}
        </>
    );
});

const propertyListSectionStyles = (theme: Theme) => css`
    position: relative;
    z-index: 1;

    ${mb(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const contactSectionStyle = css`
    ${mb(2)};
`;

const nearbyOffersHolder = (theme: Theme) => css`
    ${mb(5)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(10)}
    }
`;

const offerNavBarWrapperStyle = (theme: Theme) => css`
    margin: 0 0 0.3rem 0;
    ${display("none")};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
        ${display("block")};
    }
`;

const navBarContentStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) and (max-width: 1310px) {
        margin-top: 60px; // other bars height
        border-top: 1px solid ${getThemeVariable("colors.gray.400")};
    }
`;

const promotionsStyle = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        margin: 0 0 6rem 0;
        ${mb(7)};
    }
`;

const detailsSectionMargin = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const opinionsWrapper = css`
    ${mt(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        width: calc(288px * 3 + 1.6rem * 2); // PropertyBox width times 3 + margin between boxes times 2
        margin: ${calculateRemSize(3)} auto 0 auto;
    }
`;

const timeStampSectionStyle = (theme: Theme) => css`
    ${mb(1)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)}
    }
`;

const detailConstructionSectionStyle = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;

const offerDetailAmenitiesSection = (theme: Theme) => css`
    ${mb(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${mb(3)};
    }
`;
const mapSectionContainer = (theme: Theme) => css`
    min-height: 58rem;
    @media (min-width: ${theme.breakpoints.md}) {
        min-height: 55rem;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        min-height: 68rem;
    }
`;
